/**
 * Analytics Delayed Loader
 *
 * Purpose: Delay loading of analytics scripts (GTM, PostHog) until after
 * the page becomes interactive to improve Core Web Vitals (LCP, TBT).
 *
 * Strategy: Load analytics after 3 seconds OR on first user interaction,
 * whichever comes first. This saves ~1.5s of main thread blocking time.
 *
 * Trade-off: Analytics may miss the first few seconds of user activity,
 * but this is acceptable for better performance.
 */

(function() {
  'use strict';

  // Skip in localhost/development
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname.includes('127.0.0.1');

  if (isLocalhost) {
    window.debugLogger.log('[Analytics] Skipping analytics on localhost');
    return;
  }

  // Track if analytics have been loaded
  let analyticsLoaded = false;

  /**
   * Load Google Tag Manager
   */
  function loadGTM() {
    if (window.gtmLoaded) return;
    window.gtmLoaded = true;

    (function(w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-NZN4KS9P');

    window.debugLogger.log('[Analytics] GTM loaded');
  }

  /**
   * Load PostHog Analytics
   */
  function loadPostHog() {
    if (window.posthogLoaded) return;
    window.posthogLoaded = true;

    // Detect environment
    const isDevelopment = window.location.hostname.includes('.myshopify.com');
    const environment = isDevelopment ? 'development' : 'production';

    // Load PostHog SDK
    !function(t, e) {
      var o, n, p, r;
      e.__SV || (window.posthog = e, e._i = [], e.init = function(i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && (t = t[o[0]], e = o[1]), t[e] = function() {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
          }
        }
        (p = t.createElement("script")).type = "text/javascript", p.async = !0, p.src = s.api_host + "/static/array.js", (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (void 0 !== a ? u = e[a] = [] : a = "posthog", u.people = u.people || [], u.toString = function(t) {
          var e = "posthog";
          return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e
        }, u.people.toString = function() {
          return u.toString(1) + ".people (stub)"
        }, o = "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "), n = 0; n < o.length; n++) g(u, o[n]);
        e._i.push([i, s, a])
      }, e.__SV = 1)
    }(document, window.posthog || []);

    // Initialize PostHog
    posthog.init('phc_Tayt0MNUAfRmbFPQu5Ehg4sa73GAHY5mXEYNWtWAQqT', {
      api_host: 'https://us.i.posthog.com',
      loaded: function(posthog) {
        posthog.register({
          environment: environment,
          shopify_shop: window.Shopify?.shop || 'unknown',
          shopify_theme_id: window.Shopify?.theme?.id || 'unknown',
          shopify_currency: window.Shopify?.currency?.active || 'USD'
        });
      },
      session_recording: {
        enabled: true,
        recordCrossOriginIframes: false,
        maskAllInputs: true,
        maskTextSelector: '.private',
        recordCanvas: false,
        sample_rate: environment === 'production' ? 0.1 : 1.0,
        minimum_duration: 5000,
      },
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      respect_dnt: true,
      opt_out_capturing_by_default: false,
      persistence: 'localStorage',
      enable_recording_console_log: true,
      disable_session_recording: false,
      property_blacklist: ['password', 'credit_card', 'ssn', 'cvv'],
      disable_toolbar: environment === 'production'
    });

    // Identify user if logged in
    if (window.Shopify?.customerHash) {
      posthog.identify(window.Shopify.customerHash, {
        customer_id: window.Shopify.customerHash,
      });
    }

    // Error tracking
    window.addEventListener('error', function(event) {
      if (window.posthog) {
        posthog.capture('$exception', {
          $exception_type: event.error?.name || 'Error',
          $exception_message: event.error?.message || event.message,
          $exception_stack_trace: event.error?.stack,
          $exception_filename: event.filename,
          $exception_lineno: event.lineno,
          $exception_colno: event.colno,
          error_level: 'error'
        });
      }
    });

    window.addEventListener('unhandledrejection', function(event) {
      if (window.posthog) {
        posthog.capture('$exception', {
          $exception_type: 'UnhandledPromiseRejection',
          $exception_message: event.reason?.message || String(event.reason),
          $exception_stack_trace: event.reason?.stack,
          error_level: 'error'
        });
      }
    });

    window.debugLogger.log('[Analytics] PostHog loaded');
  }

  /**
   * Load all analytics
   */
  function loadAllAnalytics() {
    if (analyticsLoaded) return;
    analyticsLoaded = true;

    window.debugLogger.log('[Analytics] Loading analytics (delayed)...');

    // Load GTM first, then PostHog
    loadGTM();

    // Small delay between loads to avoid overwhelming the main thread
    setTimeout(loadPostHog, 100);
  }

  /**
   * User interaction events that trigger analytics loading
   */
  const interactionEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

  function onUserInteraction() {
    loadAllAnalytics();
    // Remove all listeners after first interaction
    interactionEvents.forEach(event => {
      document.removeEventListener(event, onUserInteraction, { passive: true });
    });
  }

  // Strategy: Load after 3 seconds OR on first user interaction
  // This balances performance with analytics coverage

  // 1. Set timeout for 3 seconds
  const timeoutId = setTimeout(() => {
    loadAllAnalytics();
    // Clean up interaction listeners if timeout fires first
    interactionEvents.forEach(event => {
      document.removeEventListener(event, onUserInteraction, { passive: true });
    });
  }, 3000);

  // 2. Listen for user interaction
  interactionEvents.forEach(event => {
    document.addEventListener(event, function handler() {
      clearTimeout(timeoutId); // Cancel the timeout
      onUserInteraction();
    }, { passive: true, once: true });
  });

  // 3. Also load when page becomes visible (if user switches tabs)
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible' && !analyticsLoaded) {
      // User came back to the tab - give them a moment then load
      setTimeout(loadAllAnalytics, 1000);
    }
  }, { once: true });

})();
